// 농심DAS — 작업 목록(거르기 탭 · 표 · «새 작업» 창). 작업 한 건(준비·스캔·마감)은 PdasJobPage(/pdas/:id).
// 메뉴 노출·URL 가드는 utils/menuAccess(시스템 관리자 + 명시 허용) · 실제 차단은 서버(require_menu("pdas")).
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppShell from '../components/AppShell';
import { pdasAPI } from '../api/client';
import { COLORS, btn, card, input } from '../utils/uiStyles';
import { fmtKstDateTime } from '../utils/kstTime';
import { JOB_STATUS_LABELS, errorText, labelOf } from './pdas/pdasLabels';

const TABS = [['draft', '준비'], ['active', '작업 중'], ['closed', '마감'], ['all', '전체']];
const HEADERS = ['작업 이름', '상태', '셀 수', '묶음', '단위', '만든 날'];
const TITLE_MAX = 200;
const CELLS_MAX = 500;
const th = { padding: '9px', textAlign: 'left', fontSize: '12px', color: COLORS.textSub, whiteSpace: 'nowrap', borderBottom: `2px solid ${COLORS.border}` };
const td = { padding: '8px 9px', fontSize: '13px', borderBottom: `1px solid ${COLORS.borderSoft}`, whiteSpace: 'nowrap' };

const validCells = (text) => {
  const n = Number(text);
  return /^\d+$/.test(String(text).trim()) && n >= 1 && n <= CELLS_MAX;
};

function NewJobModal({ onClose, onCreated }) {
  const [title, setTitle] = useState('');
  const [cells, setCells] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const canSave = !saving && title.trim().length > 0 && title.trim().length <= TITLE_MAX && validCells(cells);

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError('');
    try {
      const r = await pdasAPI.create({ title: title.trim(), cell_count: Number(cells) });
      onCreated(r.data);
    } catch (err) {
      setError(await errorText(err, '작업을 만들지 못했습니다.'));
      setSaving(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '12px' }}>
      <div role="dialog" aria-modal="true" aria-label="새 작업"
        onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
        style={{ ...card, width: '100%', maxWidth: '380px' }}>
        <h3 style={{ margin: '0 0 12px', fontSize: '16px' }}>새 작업</h3>
        <label style={{ display: 'block', fontSize: '12px', marginBottom: '10px' }}>작업 이름
          <input aria-label="작업 이름" value={title} maxLength={TITLE_MAX} autoFocus
            onChange={(e) => setTitle(e.target.value)} style={{ ...input, marginTop: '4px' }} />
        </label>
        <label style={{ display: 'block', fontSize: '12px', marginBottom: '10px' }}>셀 수(1~{CELLS_MAX})
          <input aria-label="셀 수" type="number" min={1} max={CELLS_MAX} inputMode="numeric" value={cells}
            onChange={(e) => setCells(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } }}
            style={{ ...input, marginTop: '4px' }} />
        </label>
        {error && <div role="alert" style={{ color: COLORS.dangerText, fontSize: '12px', marginBottom: '10px' }}>{error}</div>}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
          <button type="button" onClick={onClose} style={btn('secondary', 'sm')}>취소</button>
          <button type="button" onClick={save} disabled={!canSave}
            style={{ ...btn('primary', 'sm'), opacity: canSave ? 1 : 0.5, cursor: canSave ? 'pointer' : 'not-allowed' }}>
            {saving ? '만드는 중…' : '만들기'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function PdasPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState('active');
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [creating, setCreating] = useState(false);
  const mountedRef = useRef(true);
  const seqRef = useRef(0);
  // 처음 한 번만 — 기본 «작업 중»이 0건이면 «준비»로 옮긴다.
  const firstLoadRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; seqRef.current += 1; };
  }, []);

  useEffect(() => {
    const seq = seqRef.current + 1;
    seqRef.current = seq;
    const isCurrent = () => mountedRef.current && seqRef.current === seq;
    setBusy(true);
    setError('');
    pdasAPI.jobs(tab)
      .then((r) => {
        if (!isCurrent()) return;
        const list = Array.isArray(r.data?.items) ? r.data.items : [];
        if (firstLoadRef.current) {
          firstLoadRef.current = false;
          if (tab === 'active' && list.length === 0) { setTab('draft'); return; }
        }
        setItems(list);
      })
      .catch(async (err) => {
        const message = await errorText(err, '작업 목록을 불러오지 못했습니다.');
        if (!isCurrent()) return;
        firstLoadRef.current = false;
        setItems(null);
        setError(message);
      })
      .finally(() => { if (isCurrent()) setBusy(false); });
  }, [tab]);

  const openJob = (jobId) => navigate(`/pdas/${jobId}`);

  const onCreated = (job) => {
    setCreating(false);
    if (job && job.id !== undefined && job.id !== null) openJob(job.id);
  };

  const rows = items || [];

  return (
    <AppShell app="pdas">
      <main style={{ flex: 1, minWidth: 0, height: 'var(--gw-app-h, 100dvh)', overflow: 'auto', background: COLORS.bg }}>
        <div style={{ padding: '16px', maxWidth: '1200px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px' }}>
            <h2 style={{ margin: 0, fontSize: '20px' }}>농심DAS</h2>
            {busy && <span role="status" style={{ color: COLORS.textMuted, fontSize: '12px' }}>불러오는 중…</span>}
            <button type="button" onClick={() => setCreating(true)} style={{ marginLeft: 'auto', ...btn('primary', 'sm') }}>새 작업</button>
          </div>

          <div role="tablist" aria-label="상태" style={{ display: 'flex', gap: '4px', borderBottom: `1px solid ${COLORS.border}`, marginBottom: '12px' }}>
            {TABS.map(([key, label]) => (
              <button key={key} type="button" role="tab" aria-selected={tab === key}
                onClick={() => setTab(key)}
                style={{ padding: '8px 14px', border: 'none', borderBottom: `2px solid ${tab === key ? COLORS.primary : 'transparent'}`, background: 'none', cursor: 'pointer', fontSize: '13px', fontWeight: tab === key ? 700 : 500, color: tab === key ? COLORS.primaryDark : COLORS.textSub }}>
                {label}
              </button>
            ))}
          </div>

          {error && <div role="alert" style={{ ...card, background: COLORS.dangerSoft, color: COLORS.dangerText, marginBottom: '12px' }}>{error}</div>}

          <section style={{ ...card, padding: 0, overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto' }}>
              <table aria-label="작업 목록" style={{ width: '100%', borderCollapse: 'collapse', minWidth: '640px' }}>
                <thead><tr>{HEADERS.map((h) => <th key={h} style={th}>{h}</th>)}</tr></thead>
                <tbody>
                  {rows.map((it) => {
                    const p = it.progress || {};
                    return (
                      <tr key={it.id} data-testid="pdas-job-row" onClick={() => openJob(it.id)} style={{ cursor: 'pointer' }}>
                        <td style={{ ...td, fontWeight: 600, color: COLORS.primaryDark }}>{it.title}</td>
                        <td style={td}>{labelOf(JOB_STATUS_LABELS, it.status)}</td>
                        <td style={td}>{it.cell_count}</td>
                        <td style={td}>빼냄 {p.removed || 0} / 전체 {p.bundles_total || 0}</td>
                        <td style={td}>{p.units_done || 0} / {p.units_plan || 0}</td>
                        <td style={td}>{fmtKstDateTime(it.created_at) || '—'}</td>
                      </tr>
                    );
                  })}
                  {items && !error && rows.length === 0 && (
                    <tr><td colSpan={HEADERS.length} style={{ ...td, color: COLORS.textMuted, textAlign: 'center', padding: '24px' }}>작업이 없습니다. ‹새 작업›으로 시작하세요.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </main>
      {creating && <NewJobModal onClose={() => setCreating(false)} onCreated={onCreated} />}
    </AppShell>
  );
}
